import { Planting } from '@/types/Component';
import moment from 'moment';
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import { toCamel } from 'snake-camel';

export type SelectedPlantingAgriFieldPair = {
    planting: {
        id: string;
        name: string;
        cropFamicId: string;
    };
    agriField: {
        id: string;
        name: string;
        plantedArea: number;
    };
};

type Row = {
    crop: { id: string; name: string };
    planting: { id: string; name: string; cropFamicId: string; workStartDate: Date; workEndDate: Date };
    agriField: { id: string; name: string; plantedArea: number; displayStart: Date | null; displayEnd: Date | null; existingShippingItemId: string | null; existingShippingItemName: string | null };
};

type SortField = 'crop' | 'planting' | 'term' | 'agriField' | 'agriFieldTerm' | 'sitem';

@Component
export default class SelectPlantingsAndAgriFields extends Vue {
    @Prop()
    private value?: boolean;

    @Emit('input')
    input(value: boolean): void {
        value
    }

    @Prop()
    private pdTenantId?: string;

    @Prop()
    private tenantId?: string;

    @Prop()
    private sitemId?: string;

    @Prop()
    private itemName?: string;

    @Prop()
    private workDate?: Date;

    @Prop()
    private preSelectPairs?: SelectedPlantingAgriFieldPair[];

    private plantings: Array<Planting> = [];
    private rows: Row[] = [];

    private outOfTerm = true;
    private selectedCropId = '';
    private selectedPlantingId = '';
    private selectedAgriFieldId = '';

    private selectedKeys: Set<string> = new Set();

    private sortField: SortField = 'crop';
    private sortOrder: 'asc' | 'desc' = 'asc';

    @Watch('value')
    async whenOpen(): Promise<void> {
        if (this.value) {
            const url = this.pdTenantId
                ? `/plantings/plantings_by_cropId?tenant_id=${this.pdTenantId}&sitem_id=${this.sitemId}&dantai_tenant_id=${this.tenantId}`
                : '/plantings';
            const res = toCamel(await this.$apiClient.doRequest('get', url)) as { plantings: Planting[] };
            this.plantings = res.plantings;
            this.buildRows();
            if (this.preSelectPairs && this.preSelectPairs.length > 0) {
                this.selectedKeys = new Set(this.preSelectPairs.map(p => `${p.planting.id}_${p.agriField.id}`));
            }
        } else {
            this.reset();
        }
    }

    existingShippingItemName(row: Row): string {
        return row.agriField.existingShippingItemName || '';
    }

    isRowBlocked(row: Row): boolean {
        const existingId = row.agriField.existingShippingItemId;
        return !!existingId && existingId !== this.sitemId && !this.isRowSelected(row);
    }

    private isWithinTheTerm(start: Date | null | undefined, end: Date | null | undefined) {
        const workDate = moment(this.workDate);
        const startDate = moment(start);
        const endDate = moment(end);
        return (!startDate.isValid() || workDate.isSameOrAfter(startDate)) && (!endDate.isValid() || workDate.isSameOrBefore(endDate));
    }

    @Watch('outOfTerm')
    private buildRows() {
        const source = this.outOfTerm
            ? this.plantings
            : this.plantings.filter(p => this.isWithinTheTerm(p.workStartDate, p.workEndDate));

        this.rows = source.flatMap(p => {
            const agriFields = this.outOfTerm
                ? p.agriFields
                : p.agriFields.filter(af => this.isWithinTheTerm(af.displayStart, af.displayEnd));
            return agriFields.map(af => ({
                crop: { id: p.crop.id, name: p.crop.name },
                planting: { id: p.id, name: p.name, cropFamicId: p.cropFamicId, workStartDate: p.workStartDate, workEndDate: p.workEndDate },
                agriField: { id: af.id, name: af.name, plantedArea: af.plantedArea, displayStart: af.displayStart ?? null, displayEnd: af.displayEnd ?? null, existingShippingItemId: af.existingShippingItemId ?? null, existingShippingItemName: af.existingShippingItemName ?? null }
            }));
        });

        if (this.selectedCropId && !this.rows.some(r => r.crop.id === this.selectedCropId)) {
            this.selectedCropId = '';
        }
        if (this.selectedPlantingId && !this.rows.some(r => r.planting.id === this.selectedPlantingId)) {
            this.selectedPlantingId = '';
        }
        if (this.selectedAgriFieldId && !this.rows.some(r => r.agriField.id === this.selectedAgriFieldId)) {
            this.selectedAgriFieldId = '';
        }

        this.applySort();
    }

    get cropOptions(): Array<{ id: string; name: string }> {
        return [...new Map(this.rows.map(r => [r.crop.id, r.crop])).values()];
    }

    get plantingOptions(): Array<{ id: string; name: string }> {
        return [...new Map(this.rows.map(r => [r.planting.id, r.planting])).values()];
    }

    get agriFieldOptions(): Array<{ id: string; name: string }> {
        return [...new Map(this.rows.map(r => [r.agriField.id, r.agriField])).values()];
    }

    get filteredRows(): Row[] {
        return this.rows.filter(r =>
            (!this.selectedCropId || r.crop.id === this.selectedCropId) &&
            (!this.selectedPlantingId || r.planting.id === this.selectedPlantingId) &&
            (!this.selectedAgriFieldId || r.agriField.id === this.selectedAgriFieldId)
        );
    }

    rowKey(row: Row): string {
        return `${row.planting.id}_${row.agriField.id}`;
    }

    isRowSelected(row: Row): boolean {
        return this.selectedKeys.has(this.rowKey(row));
    }

    toggleRow(row: Row) {
        const key = this.rowKey(row);
        if (this.selectedKeys.has(key)) {
            this.selectedKeys.delete(key);
        } else {
            this.selectedKeys.add(key);
        }
        this.selectedKeys = new Set(this.selectedKeys);
    }

    get isAllSelected(): boolean {
        const selectableRows = this.filteredRows.filter(r => !this.isRowBlocked(r));
        return selectableRows.length > 0 && selectableRows.every(r => this.isRowSelected(r));
    }

    toggleSelectAll() {
        const selectableRows = this.filteredRows.filter(r => !this.isRowBlocked(r));
        if (this.isAllSelected) {
            selectableRows.forEach(r => this.selectedKeys.delete(this.rowKey(r)));
        } else {
            selectableRows.forEach(r => this.selectedKeys.add(this.rowKey(r)));
        }
        this.selectedKeys = new Set(this.selectedKeys);
    }

    isSortActive(field: SortField): boolean {
        return this.sortField === field;
    }

    isSortAscending(field: SortField): boolean {
        return this.isSortActive(field) && this.sortOrder === 'asc';
    }

    isSortDescending(field: SortField): boolean {
        return this.isSortActive(field) && this.sortOrder === 'desc';
    }

    sortBy(field: SortField) {
        this.sortOrder = this.sortField === field && this.sortOrder === 'asc' ? 'desc' : 'asc';
        this.sortField = field;
        this.applySort();
    }

    private compareDates(aDate: Date | null | undefined, bDate: Date | null | undefined, dir: number): number {
        const aValid = moment(aDate).isValid();
        const bValid = moment(bDate).isValid();
        if (!aValid && !bValid) return 0;
        if (!aValid) return 1;
        if (!bValid) return -1;
        return (moment(aDate).valueOf() - moment(bDate).valueOf()) * dir;
    }

    private applySort() {
        const dir = this.sortOrder === 'asc' ? 1 : -1;
        const field = this.sortField;
        this.rows.sort((a, b) => {
            if (field === 'term') {
                return this.compareDates(a.planting.workStartDate, b.planting.workStartDate, dir);
            }
            if (field === 'agriFieldTerm') {
                return this.compareDates(a.agriField.displayStart, b.agriField.displayStart, dir);
            }
            if (field === 'sitem') {
                return this.existingShippingItemName(a).localeCompare(this.existingShippingItemName(b), 'ja') * dir;
            }
            return a[field].name.localeCompare(b[field].name, 'ja') * dir;
        });
    }

    formatWorkDate(date: Date): string {
        return moment(date).format('YYYY/MM/DD');
    }

    formatTerm(row: Row): string {
        return `${this.formatWorkDate(row.planting.workStartDate)}〜${this.formatWorkDate(row.planting.workEndDate)}`;
    }

    formatAgriFieldTerm(row: Row): string {
        if (!row.agriField.displayStart || !row.agriField.displayEnd) {
            return '';
        }
        return `${this.formatWorkDate(row.agriField.displayStart)}〜${this.formatWorkDate(row.agriField.displayEnd)}`;
    }

    get referenceDateLabel(): string {
        return this.workDate ? this.formatWorkDate(this.workDate) : '';
    }

    get modalTitle(): string {
        return this.itemName ? `作付・圃場の選択 （指定品目：${this.itemName}）` : '作付・圃場の選択';
    }

    get hasSelection(): boolean {
        return this.selectedKeys.size > 0;
    }

    @Emit('selectedItem')
    selectedItem(): SelectedPlantingAgriFieldPair[] {
        this.input(false);
        return this.rows
            .filter(r => this.selectedKeys.has(this.rowKey(r)))
            .map(r => ({
                planting: { id: r.planting.id, name: r.planting.name, cropFamicId: r.planting.cropFamicId },
                agriField: { id: r.agriField.id, name: r.agriField.name, plantedArea: r.agriField.plantedArea }
            }));
    }

    private cancel() {
        this.input(false);
    }

    private reset() {
        this.plantings = [];
        this.rows = [];
        this.selectedKeys = new Set();
        this.selectedCropId = '';
        this.selectedPlantingId = '';
        this.selectedAgriFieldId = '';
        this.outOfTerm = true;
        this.sortField = 'crop';
        this.sortOrder = 'asc';
        (this.$refs.filterAccordion as HTMLElement | undefined)?.classList.remove('open');
    }

    get isModalVisible(): boolean {
        return this.value ? this.value : false
    }

    set isModalVisible(value: boolean) {
        this.input(value)
    }
}
